const AUTHENTICATED_SESSION_MARKER_KEY = 'issen_authenticated_session_v1'
const SKIP_BOOT_SPINNER_KEY = 'issen_skip_boot_spinner'

type AuthenticationFailureHandler = () => void

type StorageSetItem = (this: Storage, key: string, value: string) => void

let authenticationFailureHandler: AuthenticationFailureHandler | null = null
let authenticationFailureDispatched = false
let authenticatedInMemory = false
let clientStorageWritesBlocked = false
let originalStorageSetItem: StorageSetItem | null = null
let storageWriteBlockInstalled = false

function localStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function markAuthenticatedSession(): void {
  allowClientStorageWrites()
  authenticationFailureDispatched = false
  authenticatedInMemory = true
  const storage = localStorageOrNull()
  try {
    storage?.setItem(AUTHENTICATED_SESSION_MARKER_KEY, '1')
  } catch {
    // The in-memory marker still permits the current page to recover.
  }
}

export function clearAuthenticatedSession(): void {
  authenticatedInMemory = false
  const storage = localStorageOrNull()
  try {
    storage?.removeItem(AUTHENTICATED_SESSION_MARKER_KEY)
  } catch {
    // A stale non-secret marker is harmless; the one-shot guard still applies.
  }
}

function sessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function wipeClientStorage(): void {
  clearAuthenticatedSession()
  const localStorage = localStorageOrNull()
  const sessionStorage = sessionStorageOrNull()
  try {
    localStorage?.clear()
  } catch {
    // Best-effort wipe; remaining keys must not keep the next visitor logged in.
  }
  try {
    sessionStorage?.clear()
  } catch {
    // Best-effort wipe.
  }
}

export function isLogoutRedirectInProgress(): boolean {
  const sessionStorage = sessionStorageOrNull()
  try {
    return sessionStorage?.getItem(SKIP_BOOT_SPINNER_KEY) === '1'
  } catch {
    return false
  }
}

export function areClientStorageWritesBlocked(): boolean {
  return clientStorageWritesBlocked
}

function shouldBlockStorageWrite(key: string): boolean {
  if (clientStorageWritesBlocked === false) {
    return false
  }
  return key !== SKIP_BOOT_SPINNER_KEY
}

function writeThroughOriginalSetItem(storage: Storage, key: string, value: string): void {
  if (originalStorageSetItem === null) {
    return
  }
  originalStorageSetItem.call(storage, key, value)
}

function overlayStorageSetItem(storage: Storage): void {
  try {
    Object.defineProperty(storage, 'setItem', {
      configurable: true,
      writable: true,
      value: (key: string, value: string): void => {
        if (shouldBlockStorageWrite(key)) {
          return
        }
        writeThroughOriginalSetItem(storage, key, value)
      },
    })
  } catch {
    // Some browsers freeze Storage methods; the prototype patch still applies.
  }
}

function installStorageWriteBlock(): void {
  if (storageWriteBlockInstalled || typeof Storage === 'undefined') {
    return
  }
  originalStorageSetItem = Storage.prototype.setItem
  Storage.prototype.setItem = function (this: Storage, key: string, value: string): void {
    if (shouldBlockStorageWrite(key)) {
      return
    }
    writeThroughOriginalSetItem(this, key, value)
  }
  const localStorage = localStorageOrNull()
  const sessionStorage = sessionStorageOrNull()
  if (localStorage !== null) {
    overlayStorageSetItem(localStorage)
  }
  if (sessionStorage !== null) {
    overlayStorageSetItem(sessionStorage)
  }
  storageWriteBlockInstalled = true
}

function allowClientStorageWrites(): void {
  clientStorageWritesBlocked = false
}

export function preventClientStorageWritesAfterLogout(): void {
  clientStorageWritesBlocked = true
  installStorageWriteBlock()
}

export function restoreClientStorageWritesForTests(): void {
  if (originalStorageSetItem !== null && typeof Storage !== 'undefined') {
    Storage.prototype.setItem = originalStorageSetItem
  }
  originalStorageSetItem = null
  storageWriteBlockInstalled = false
  clientStorageWritesBlocked = false
  const localStorage = localStorageOrNull()
  const sessionStorage = sessionStorageOrNull()
  try {
    if (localStorage !== null) {
      Reflect.deleteProperty(localStorage, 'setItem')
    }
  } catch {
    // Test cleanup is best-effort.
  }
  try {
    if (sessionStorage !== null) {
      Reflect.deleteProperty(sessionStorage, 'setItem')
    }
  } catch {
    // Test cleanup is best-effort.
  }
}

export function markSkipBootSpinner(): void {
  const sessionStorage = sessionStorageOrNull()
  try {
    sessionStorage?.setItem(SKIP_BOOT_SPINNER_KEY, '1')
  } catch {
    // Showing the boot spinner once is better than failing logout.
  }
}

export function consumeSkipBootSpinner(): boolean {
  const sessionStorage = sessionStorageOrNull()
  try {
    if (sessionStorage?.getItem(SKIP_BOOT_SPINNER_KEY) !== '1') {
      return false
    }
    sessionStorage.removeItem(SKIP_BOOT_SPINNER_KEY)
    return true
  } catch {
    return false
  }
}

export function consumeAuthenticationFailure(): boolean {
  if (authenticationFailureDispatched) {
    return false
  }
  const storage = localStorageOrNull()
  let hadAuthenticatedSession = authenticatedInMemory
  try {
    hadAuthenticatedSession =
      hadAuthenticatedSession ||
      storage?.getItem(AUTHENTICATED_SESSION_MARKER_KEY) === '1'
  } catch {
    // Retain the in-memory authenticated state.
  }
  if (!hadAuthenticatedSession) {
    return false
  }
  authenticationFailureDispatched = true
  clearAuthenticatedSession()
  return true
}

export function setAuthenticationFailureHandler(
  handler: AuthenticationFailureHandler | null,
): void {
  authenticationFailureHandler = handler
}

export function notifyAuthenticationFailure(): void {
  authenticationFailureHandler?.()
}
